import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, FileText, Puzzle, Search } from "lucide-react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  useGetPromptsQuery,
  useGetPromptQuery,
  useUpdatePromptMutation,
  useArchivePromptMutation,
  useRestorePromptMutation,
  useAddPromptVersionMutation,
  useUpdatePromptVersionStatusMutation,
} from "@/store/admin_api";
import type { PromptKind, PromptVersion, SkillAudience, SkillScope, SkillShape, SkillType } from "@/model/interface/Prompt";
import { apiErrorMessage } from "@/util/apiError";
import CreatePromptDialog, { CREATE_PROMPT_COPY } from "./components/CreatePromptDialog";
import SkillShapeFields, { SKILL_TYPE_COPY } from "./components/SkillShapeFields";

const KIND_COPY: Record<PromptKind, { blurb: string; empty: string }> = {
  AGENT: {
    blurb: "Shared, versioned personas. Assign a version to a template or to a single agent.",
    empty: "No prompts yet.",
  },
  SKILL: {
    blurb:
      "Focused capability blocks appended to an agent's prompt. Global skills reach every agent; agent skills reach only the templates and agents you add them to. Agents follow the latest published version, so publishing an edit reaches every agent using it.",
    empty: "No skills yet.",
  },
};

function statusPill(status: string) {
  if (status === "PUBLISHED") return "status-pill-success";
  if (status === "ARCHIVED") return "status-pill-neutral";
  return "status-pill-warning"; // DRAFT
}

/** Small POLICY/PHASE marker, so a list of skills reads at a glance. */
function SkillTypeBadge({ type }: { type?: SkillType }) {
  const resolved: SkillType = type === "PHASE" ? "PHASE" : "POLICY";
  return (
    <span
      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
        resolved === "PHASE"
          ? "bg-primary/10 text-primary"
          : "bg-muted text-muted-foreground"
      }`}
    >
      {SKILL_TYPE_COPY[resolved].label}
    </span>
  );
}

/**
 * Shown only for the narrower scope — the default needs no marker, and badging every row would
 * bury the one thing worth spotting in a list.
 */
function SkillScopeBadge({ type, scope }: { type?: SkillType; scope?: SkillScope }) {
  if (type === "PHASE" || scope !== "ROUTER_ONLY") return null;
  return (
    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      Pre-stage
    </span>
  );
}

function SkillAudienceBadge({ audience }: { audience?: SkillAudience }) {
  if (audience !== "GLOBAL") {
    return null;
  }
  return (
    <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
      Global
    </span>
  );
}

/** Right pane: a prompt's version history + add-version editor. */
function PromptDetail({ promptId, kind }: { promptId: string; kind: PromptKind }) {
  const { data: prompt, isLoading } = useGetPromptQuery(promptId);
  const [addVersion, { isLoading: isAdding }] = useAddPromptVersionMutation();
  const [updateStatus] = useUpdatePromptVersionStatusMutation();
  const [updatePrompt, { isLoading: isSavingShape }] = useUpdatePromptMutation();
  const [archivePrompt, { isLoading: isArchiving }] = useArchivePromptMutation();
  const [restorePrompt, { isLoading: isRestoring }] = useRestorePromptMutation();

  const [draft, setDraft] = useState("");
  const [label, setLabel] = useState("");
  const [viewing, setViewing] = useState<PromptVersion | null>(null);

  // Shape is family metadata, not versioned — editing it takes effect on the next call, the same
  // way publishing a new text version does.
  const [shape, setShape] = useState<SkillShape | null>(null);
  const activeShape: SkillShape = shape ?? {
    skillType: (prompt?.skillType ?? "POLICY") as SkillType,
    skillScope: (prompt?.skillScope ?? "ALL_MEMBERS") as SkillScope,
    skillAudience: prompt?.skillAudience === "GLOBAL" ? "GLOBAL" : "AGENT",
    memberTools: prompt?.memberTools ?? [],
    handoffDescription: prompt?.handoffDescription ?? "",
    openingLine: prompt?.openingLine ?? "",
  };

  if (isLoading || !prompt) return <Skeleton className="h-96 w-full" />;

  const handleSaveShape = async () => {
    if (activeShape.skillType === "PHASE" && !activeShape.handoffDescription.trim()) {
      toast.error("A call stage needs a hand-off description");
      return;
    }
    try {
      await updatePrompt({
        id: promptId,
        skillType: activeShape.skillType,
        skillScope: activeShape.skillScope,
        skillAudience: activeShape.skillAudience,
        memberTools: activeShape.memberTools,
        handoffDescription: activeShape.handoffDescription.trim() || undefined,
        openingLine: activeShape.openingLine.trim() || undefined,
      }).unwrap();
      toast.success("Skill settings saved");
      setShape(null);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to save skill settings"));
    }
  };

  const handleAdd = async () => {
    if (!draft.trim()) return;
    try {
      await addVersion({ promptId, systemPrompt: draft, label: label.trim() || undefined }).unwrap();
      toast.success("New version added");
      setDraft("");
      setLabel("");
    } catch (e: any) {
      toast.error(e?.data?.detail || "Failed to add version");
    }
  };

  const handleStatus = async (versionId: string, status: string) => {
    try {
      await updateStatus({ promptId, versionId, status }).unwrap();
      toast.success(`Marked ${status.toLowerCase()}`);
    } catch (e: any) {
      toast.error(e?.data?.detail || "Failed to update status");
    }
  };

  const handleArchiveToggle = async () => {
    const label = kind === "SKILL" ? "Skill" : "Prompt";
    try {
      if (prompt.archivedAt) {
        await restorePrompt(promptId).unwrap();
        toast.success(`${label} restored`);
      } else {
        await archivePrompt(promptId).unwrap();
        toast.success(`${label} archived`);
      }
    } catch (e: any) {
      toast.error(e?.data?.detail || "Failed to update");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">{prompt.name}</h3>
            {kind === "SKILL" && <SkillAudienceBadge audience={prompt.skillAudience} />}
            {kind === "SKILL" && <SkillTypeBadge type={prompt.skillType} />}
            {kind === "SKILL" && <SkillScopeBadge type={prompt.skillType} scope={prompt.skillScope} />}
            {prompt.archivedAt && <span className="status-pill-neutral">archived</span>}
          </div>
          {prompt.description && <p className="text-sm text-muted-foreground">{prompt.description}</p>}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          onClick={handleArchiveToggle}
          disabled={isArchiving || isRestoring}
        >
          {prompt.archivedAt ? "Restore" : "Archive"}
        </Button>
      </div>

      {prompt.archivedAt && (
        <p className="rounded-lg border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {kind === "SKILL" && prompt.skillAudience === "GLOBAL"
            ? "Archived: a global skill stops reaching agents while it is archived."
            : `Archived: hidden when picking ${kind === "SKILL" ? "skills" : "prompts"}. Agents and templates already using it are unaffected.`}
        </p>
      )}

      {kind === "SKILL" && (
        <div className="space-y-3 rounded-lg border p-4">
          <p className="text-sm font-medium">Skill settings</p>
          <SkillShapeFields shape={activeShape} onChange={(next) => setShape({ ...activeShape, ...next })} />
          <div className="flex justify-end">
            <Button size="sm" onClick={handleSaveShape} disabled={isSavingShape || shape === null}>
              {isSavingShape ? "Saving…" : "Save settings"}
            </Button>
          </div>
        </div>
      )}

      {/* Version history */}
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Versions</p>
        {(prompt.versions ?? []).map((v) => (
          <div key={v.id} className="flex items-center gap-3 rounded-lg border px-3 py-2">
            <span className="font-mono text-sm">v{v.version}</span>
            <span className={statusPill(v.status)}>{v.status.toLowerCase()}</span>
            {v.label && <span className="text-sm text-muted-foreground truncate">{v.label}</span>}
            <span className="text-xs text-muted-foreground ml-auto shrink-0">
              {v.createdAt ? new Date(v.createdAt).toLocaleDateString() : ""}
            </span>
            <Button size="sm" variant="ghost" onClick={() => setViewing(v)}>View</Button>
            {v.status !== "PUBLISHED" && (
              <Button size="sm" variant="ghost" onClick={() => handleStatus(v.id, "PUBLISHED")}>Publish</Button>
            )}
            {v.status !== "ARCHIVED" && (
              <Button size="sm" variant="ghost" onClick={() => handleStatus(v.id, "ARCHIVED")}>Archive</Button>
            )}
          </div>
        ))}
      </div>

      {/* Add new version */}
      <div className="space-y-2 rounded-lg border p-4">
        <p className="text-sm font-medium">New version</p>
        <Input placeholder="Label (optional) — e.g. tighter qualification" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Textarea
          rows={12}
          placeholder="System prompt text…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="font-mono text-xs"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={handleAdd} disabled={isAdding || !draft.trim()}>
            {isAdding ? "Adding…" : "Add version"}
          </Button>
        </div>
      </div>

      {/* View a version's full text */}
      <Dialog open={viewing !== null} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{prompt.name} — v{viewing?.version} {viewing?.label ? `· ${viewing.label}` : ""}</DialogTitle>
          </DialogHeader>
          <pre className="whitespace-pre-wrap text-xs bg-muted rounded-md p-3">{viewing?.systemPrompt}</pre>
          <DialogFooter>
            <Button variant="outline" onClick={() => { if (viewing) { setDraft(viewing.systemPrompt); setViewing(null); } }}>
              Use as base for new version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const LIBRARY_FILTERS = [
  { value: "ACTIVE", label: "Active" },
  { value: "UNPUBLISHED", label: "No published version" },
  { value: "ARCHIVED", label: "Archived" },
];

export default function PromptsAdmin({ kind }: { kind: PromptKind }) {
  const [libraryFilter, setLibraryFilter] = useState<string>("ACTIVE");
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebouncedValue(searchText.trim(), 250);
  const { data: promptPage, isLoading } = useGetPromptsQuery({
    kind,
    archived: libraryFilter === "ARCHIVED",
    unpublished: libraryFilter === "UNPUBLISHED",
    search: debouncedSearch || undefined,
    page,
    size: 25,
  });
  const prompts = promptPage?.items;
  const totalPages = promptPage?.totalPages ?? 0;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const copy = KIND_COPY[kind];

  const changeFilter = (next: string) => {
    setLibraryFilter(next);
    setPage(0);
    setSelectedId(null);
  };

  const changeSearch = (nextSearch: string) => {
    setSearchText(nextSearch);
    setPage(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground min-w-0">{copy.blurb}</p>
        <div className="flex items-center gap-2 shrink-0">
          <Select value={libraryFilter} onValueChange={changeFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LIBRARY_FILTERS.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>{filter.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" /> {CREATE_PROMPT_COPY[kind].title}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        {/* List */}
        <div className="space-y-1">
          <div className="relative pb-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchText}
              onChange={(event) => changeSearch(event.target.value)}
              placeholder={kind === "SKILL" ? "Search skills" : "Search prompts"}
              className="pl-8"
            />
          </div>
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (prompts ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {debouncedSearch
                ? `Nothing matches "${debouncedSearch}".`
                : libraryFilter === "ACTIVE" ? copy.empty : "Nothing matches this filter."}
            </p>
          ) : (
            (prompts ?? []).map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className={`w-full text-left flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                  selectedId === p.id ? "bg-muted border-primary" : "hover:bg-muted/50"
                }`}
              >
                {kind === "SKILL" ? (
                  <Puzzle className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span className="text-sm font-medium truncate">{p.name}</span>
                {kind === "SKILL" && <SkillAudienceBadge audience={p.skillAudience} />}
                {kind === "SKILL" && <SkillTypeBadge type={p.skillType} />}
                {kind === "SKILL" && <SkillScopeBadge type={p.skillType} scope={p.skillScope} />}
                {p.activeVersion == null && (
                  <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                    Unpublished
                  </span>
                )}
              </button>
            ))
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
              <span>Page {page + 1} of {totalPages}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={promptPage?.last ?? true} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Detail */}
        <div>
          {selectedId ? (
            <PromptDetail promptId={selectedId} kind={kind} />
          ) : (
            <div className="flex items-center justify-center h-64 text-sm text-muted-foreground border rounded-lg border-dashed">
              Select {kind === "SKILL" ? "a skill" : "a prompt"} to view its versions
            </div>
          )}
        </div>
      </div>

      <CreatePromptDialog
        kind={kind}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(created) => setSelectedId(created.id)}
      />
    </div>
  );
}
